import React, { ReactNode, useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'

import { Button, Col, Drawer, Row, Steps } from 'antd'
//
import classNames from 'classnames'

import { StepStatus as ItemStatus, WizardStep } from 'core/domain/wizard/wizard'

import Collapse from 'common/components/dataDisplay/Collapse'
import Icon from 'common/components/general/Icon'

import useLoginUser from 'common/hooks/useLoginUser'

import { getNavigationItemFullPath } from './common/helpers'

const { Step } = Steps

export const DEFAULT_CATEGORY_INDEX = 0
export const DEFAULT_EDITING_CATEGORY_INDEX = -1
export const DEFAULT_CATEGORY_ITEM_INDEX = 0
export const DEFAULT_EDITING_CATEGORY_ITEM_INDEX = -1

declare type StepStatus = 'finish' | 'process' | 'wait'

interface SignUpNavigationProps {
  state: WizardStep[]
  redirectTo: (path: string) => void
  activeItem?: ActiveItemProps
  isMobile?: boolean
}

export interface ActiveItemProps {
  categoryIndex: number
  categoryItemIndex: number
}

const SignUpNavigationMenu: React.FC<SignUpNavigationProps> = (props) => {
  const [isDrawerVisible, setIsDrawerVisible] = useState<boolean>(false)

  const [editingWizardStep, setEditingWizardStep] = useState({
    categoryIndex: props.activeItem ? props.activeItem.categoryIndex : DEFAULT_EDITING_CATEGORY_INDEX,
    categoryItemIndex: props.activeItem ? props.activeItem.categoryItemIndex : DEFAULT_EDITING_CATEGORY_ITEM_INDEX,
  })
  const [activeCollapseKey, setActiveCollapseKey] = useState<string | string[]>()
  const history = useHistory()
  const [user] = useLoginUser()

  useEffect(() => {
    setEditingWizardStep({
      categoryIndex: props.activeItem ? props.activeItem.categoryIndex : DEFAULT_EDITING_CATEGORY_INDEX,
      categoryItemIndex: props.activeItem ? props.activeItem.categoryItemIndex : DEFAULT_EDITING_CATEGORY_ITEM_INDEX,
    })
    if (props.activeItem !== undefined && activeCollapseKey !== props.activeItem.categoryIndex.toString())
      setActiveCollapseKey(props.activeItem.categoryIndex.toString())
  }, [props.activeItem])

  const categoriesGenerator = (state: WizardStep[]): ReactNode => {
    if (state === undefined || state.length === 0) return null
    const activeKey = getIndexOfActiveCategory(state)
    const categoriesContent = state
      .sort((first, second) => first.priority - second.priority)
      .map((category) => {
        const categoryStatus = getCategoryStatus(category)
        return {
          key: category.priority,
          header: category.name,
          disabled: categoryStatus === ItemStatus.Disabled,
          extra: getCategoryExtraMenu(categoryStatus, category.children.length),
          content: getCategoryContent(category),
          className: classNames({
            collapse__item: true,
            collapse__item_active: categoryStatus === ItemStatus.Active,
            collapse__item_complete: categoryStatus === ItemStatus.Complete,
          }),
        }
      })

    return (
      <Collapse
        accordion={true}
        activeKey={activeCollapseKey !== undefined ? activeCollapseKey : activeKey.toString()}
        onChange={(key: string | string[]) => {
          if (key !== activeCollapseKey) setActiveCollapseKey(key)
        }}
        panels={categoriesContent ?? []}
      />
    )
  }

  const getIndexOfActiveCategory = (state: WizardStep[]): number => {
    try {
      if (editingWizardStep.categoryIndex !== DEFAULT_EDITING_CATEGORY_INDEX) {
        const category = state.find((cat) => cat.priority === editingWizardStep.categoryIndex)
        if (category === undefined) throw new Error('Category not found')
        const categoryStatus = getCategoryStatus(category)
        if (categoryStatus === ItemStatus.Active || categoryStatus === ItemStatus.Complete) {
          return editingWizardStep.categoryIndex
        } else {
          setEditingWizardStep({
            categoryIndex: DEFAULT_EDITING_CATEGORY_INDEX,
            categoryItemIndex: DEFAULT_EDITING_CATEGORY_ITEM_INDEX,
          })
        }
      }
      const category = state.find((category) => getCategoryStatus(category) === ItemStatus.Active)
      return category ? category.priority : DEFAULT_CATEGORY_INDEX
    } catch (e) {
      console.log(e)
      return DEFAULT_CATEGORY_INDEX
    }
  }

  const getCategoryStatus = (category: WizardStep): ItemStatus => {
    if (
      editingWizardStep.categoryIndex === category.priority &&
      (category.children.some((categoryItem) => categoryItem.status === ItemStatus.Active) ||
        category.children.every((categoryItem) => categoryItem.status === ItemStatus.Complete))
    ) {
      return ItemStatus.Active
    } else {
      return category.status
    }
  }

  const getCategoryExtraMenu = (categoryStatus: number, children: number): ReactNode => {
    if (categoryStatus === ItemStatus.Complete) {
      return (
        <div className="badge outline-green c-green">
          <Icon type="check" className="fa fa-solid fa-check" />
        </div>
      )
    } else if (categoryStatus === ItemStatus.Disabled) {
      return <div className="badge outline">{children}</div>
    } else if (children > 0) {
      return <div className="badge bc-green c-white">{children}</div>
    } else {
      return <></>
    }
  }

  const getCategoryContent = (category: WizardStep): ReactNode => {
    return getCategoryStatus(category) !== ItemStatus.Disabled ? categoryItemsGenerator(category) : null
  }

  const categoryItemsGenerator = (category: WizardStep): ReactNode => {
    const categoryStatus = getCategoryStatus(category)
    const activeCategoryItem = getIndexOfActiveCategoryItem(category)
    return (
      <Steps current={activeCategoryItem} direction="vertical" size="small">
        {category.children
          .sort((first, second) => first.priority - second.priority)
          .map((categoryItem, index) => {
            const categoryItemStatus = getCategoryItemStatus(category, categoryItem)
            return (
              <Step
                title={getTitleContentOfCategoryItem(categoryItem)}
                key={index}
                status={categoryItemStatus}
                className={classNames({
                  steps__item: true,
                  steps__item_active:
                    ((categoryStatus === ItemStatus.Active &&
                      editingWizardStep.categoryIndex === DEFAULT_EDITING_CATEGORY_INDEX) ||
                      editingWizardStep.categoryIndex === category.priority) &&
                    activeCategoryItem === index,
                })}
              />
            )
          })}
      </Steps>
    )
  }

  const getIndexOfActiveCategoryItem = (category: WizardStep): number => {
    try {
      if (
        editingWizardStep.categoryIndex !== DEFAULT_EDITING_CATEGORY_INDEX &&
        editingWizardStep.categoryIndex === category.priority &&
        editingWizardStep.categoryItemIndex !== DEFAULT_EDITING_CATEGORY_ITEM_INDEX
      ) {
        const categoryItem = category.children.find((item) => item.priority === editingWizardStep.categoryItemIndex)
        if (categoryItem === undefined) throw new Error('Category Item not found')
        if (categoryItem.status === ItemStatus.Active || categoryItem.status === ItemStatus.Complete) {
          return editingWizardStep.categoryItemIndex
        } else {
          setEditingWizardStep({
            categoryIndex: DEFAULT_EDITING_CATEGORY_INDEX,
            categoryItemIndex: DEFAULT_EDITING_CATEGORY_ITEM_INDEX,
          })
        }
      } else if (
        editingWizardStep.categoryIndex !== DEFAULT_EDITING_CATEGORY_INDEX &&
        editingWizardStep.categoryItemIndex !== DEFAULT_EDITING_CATEGORY_ITEM_INDEX
      ) {
        return DEFAULT_CATEGORY_ITEM_INDEX
      }

      let activeCategoryItem = category.children.find((categoryItem) => categoryItem.status === ItemStatus.Active)

      if (activeCategoryItem === undefined) return DEFAULT_CATEGORY_ITEM_INDEX

      redirectTo(activeCategoryItem)
      return activeCategoryItem.priority
    } catch (e) {
      console.log(category)
      console.log(e)
      return DEFAULT_CATEGORY_ITEM_INDEX
    }
  }

  const redirectTo = (categoryItem: WizardStep) => {
    const categoryItemPath = getNavigationItemFullPath(categoryItem, user.productId)
    if (history.location.pathname !== categoryItemPath) history.push(categoryItemPath)
  }

  const getCategoryItemStatus = (category: WizardStep, categoryItem: WizardStep): StepStatus => {
    if (
      editingWizardStep.categoryIndex === category.priority &&
      editingWizardStep.categoryItemIndex === categoryItem.priority
    ) {
      return 'process'
    } else if (editingWizardStep.categoryItemIndex >= 0 && categoryItem.status === ItemStatus.Active) {
      return 'wait'
    } else if (categoryItem.status === ItemStatus.Complete) {
      return 'finish'
    } else if (categoryItem.status === ItemStatus.Active) {
      return 'process'
    } else {
      return 'wait'
    }
  }

  const getTitleContentOfCategoryItem = (categoryItem: WizardStep): ReactNode => {
    return categoryItem.status === ItemStatus.Active || categoryItem.status === ItemStatus.Complete ? (
      <label
        className="pointer"
        onClick={() => {
          const categoryItemPath = getNavigationItemFullPath(categoryItem, user.productId)
          props.redirectTo(categoryItemPath)
        }}
      >
        {categoryItem.name}
      </label>
    ) : (
      categoryItem.name
    )
  }

  return props.isMobile ? (
    <div className="admin-signup-sticky-container pt-24">
      <Button
        className="admin-sign-up-drawer-button"
        onClick={() => {
          setIsDrawerVisible(true)
        }}
        type="text"
      >
        <Icon type="far fa-indent" fontAwesome />
      </Button>
      <Drawer
        rootClassName="admin-signup-mobile-drawer sign_up"
        width={240}
        open={isDrawerVisible}
        onClose={() => {
          setIsDrawerVisible(false)
        }}
        placement="left"
      >
        <div className="menu">{categoriesGenerator(props.state)}</div>
      </Drawer>
    </div>
  ) : (
    <section className="sign_up">
      {/* <Row>
        <Col className="progress">
          <Tooltip
            position="right"
            title={
              "Question " + getNumberOfCompletedCategoryItems(props.state) + "/" + getNumberOfCategoryItems(props.state)
            }
          >
            <Progress
              size="default"
              type="circle"
              width={120}
              percent={getPercentageOfCompletedCategoryItems(props.state)}
              strokeColor="#11d175"
            />
          </Tooltip>
        </Col>
      </Row> */}
      <Row className="pt-64">
        <Col className="menu">{categoriesGenerator(props.state)}</Col>
      </Row>
    </section>
  )
}

export default SignUpNavigationMenu
